/**
 * Meta Limited Data Use (LDU) Utility
 *
 * Implements Meta's LDU compliance for users in affected US states.
 * LDU restricts data processing for targeted advertising while still
 * allowing basic conversion tracking.
 *
 * States list is API-driven via app config with fallback to defaults.
 *
 * @see https://developers.facebook.com/docs/meta-pixel/implementation/data-processing-options
 */
// Default affected US states for LDU (fallback if API doesn't provide)
// As of Dec 2024: Rhode Island, Kentucky, Indiana
const DEFAULT_LDU_AFFECTED_STATES = ['RI', 'KY', 'IN'];
// Module-level state configured via API
let configuredAffectedStates = new Set(DEFAULT_LDU_AFFECTED_STATES);
/**
 * Configure affected states from API
 * Called when app config loads - uses API values if available, otherwise falls back to defaults
 *
 * @param states - Array of US state codes (e.g., ['RI', 'KY', 'IN'])
 */
function configureLDUStates(states) {
    if (states && states.length > 0) {
        // Normalize to uppercase for consistent matching
        configuredAffectedStates = new Set(states.map((s) => s.toUpperCase().trim()));
    }
    else {
        // Fallback to defaults if API doesn't provide states
        configuredAffectedStates = new Set(DEFAULT_LDU_AFFECTED_STATES);
    }
}
/**
 * Get the currently configured affected states
 * Useful for debugging
 */
function getConfiguredStates() {
    return Array.from(configuredAffectedStates);
}
/**
 * Check if a given US state code is in the LDU affected list
 *
 * @param stateCode - Two-letter US state code (e.g., 'RI', 'KY')
 * @returns true if state requires LDU
 */
function isAffectedState(stateCode) {
    if (!stateCode)
        return false;
    return configuredAffectedStates.has(stateCode.toUpperCase().trim());
}
/**
 * Determine if LDU should be applied based on shipping and/or billing addresses
 * Client chose: check BOTH addresses - if EITHER is in an affected state, apply LDU
 *
 * @param shippingState - Shipping address state code
 * @param billingState - Billing address state code
 * @returns true if LDU should be applied
 */
function shouldApplyLDU(shippingState, billingState) {
    return isAffectedState(shippingState) || isAffectedState(billingState);
}
/**
 * Get LDU options for Meta Pixel if applicable
 * Returns null if LDU should not be applied (user not in affected state)
 *
 * @param shippingState - Shipping address state code
 * @param billingState - Billing address state code
 * @returns LDUOptions if LDU should apply, null otherwise
 */
function getLDUOptions(shippingState, billingState) {
    if (!shouldApplyLDU(shippingState, billingState)) {
        return null;
    }
    return {
        enabled: true,
        country: 1,
        state: 0, // 0 = auto-detect state based on IP (Meta will verify)
    };
}

export { configureLDUStates, getConfiguredStates, getLDUOptions, isAffectedState, shouldApplyLDU };
//# sourceMappingURL=limitedDataUse.js.map
